import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  copyText,
  deleteTranscription,
  fetchMarkdown,
  getTranscription,
  retryTranscription,
  setSpeakerLabel,
  updateMeta,
} from "../api";
import type { TranscriptionRow } from "../types";
import { fmtAbsolute, fmtBytes, fmtCount, fmtDuration, fmtRelative, fmtTime, parseLabels } from "../format";
import { groupTurns, speakerDisplay } from "../../transcript";
import { DeleteModal } from "../components/DeleteModal";
import { Markdown } from "../components/Markdown";

const SPEAKER_HUES = [35, 200, 145, 285, 65, 320];

function speakerColor(speakerId: string): string {
  if (!speakerId) return "var(--ink-soft)";
  let h = 0;
  for (let i = 0; i < speakerId.length; i++) h = (h * 31 + speakerId.charCodeAt(i)) >>> 0;
  const hue = SPEAKER_HUES[h % SPEAKER_HUES.length];
  return `light-dark(oklch(45% 0.12 ${hue}), oklch(75% 0.13 ${hue}))`;
}

function speakerGlyph(speakerId: string): string {
  if (!speakerId) return "·";
  const m = speakerId.match(/(\d+)/);
  if (m) {
    const n = parseInt(m[1]!, 10);
    return String.fromCharCode(0x215f + n) ?? `№${n}`;
  }
  return speakerId.charAt(0).toUpperCase();
}

function matchTerms(q: string): string[] {
  return q.toLowerCase().split(/\s+/).map((t) => t.trim()).filter((t) => t.length >= 2);
}

function isMatch(word: string, terms: string[]): boolean {
  if (terms.length === 0) return false;
  const lower = word.toLowerCase();
  return terms.some((t) => lower.includes(t));
}

export function DetailPage() {
  const { id } = useParams<{ id: string }>();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const highlight = params.get("q")?.trim() ?? "";

  const [row, setRow] = useState<TranscriptionRow | null | "missing">(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [titleInput, setTitleInput] = useState("");
  const [summaryInput, setSummaryInput] = useState("");
  const [savingMeta, setSavingMeta] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [copyState, setCopyState] = useState<"idle" | "busy" | "copied" | "failed">("idle");

  const refresh = useCallback(async () => {
    if (!id) return;
    try {
      const fresh = await getTranscription(id);
      setRow(fresh);
    } catch {
      setRow("missing");
    }
  }, [id]);

  useEffect(() => { void refresh(); }, [refresh]);

  // Initialize editor inputs once
  useEffect(() => {
    if (row && row !== "missing") {
      setTitleInput(row.title ?? "");
      setSummaryInput(row.summary ?? "");
      const hasCustomTitle = !!row.title && row.title.trim().length > 0 && row.title !== row.filename;
      const hasSummary = !!row.summary && row.summary.trim().length > 0;
      if (!hasCustomTitle && !hasSummary && row.status === "done") setEditorOpen(true);
    }
  }, [row && row !== "missing" ? row.id : null]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-refresh while pending
  useEffect(() => {
    if (!row || row === "missing") return;
    if (row.status !== "queued" && row.status !== "processing") return;
    const t = setInterval(() => { void refresh(); }, 3000);
    return () => clearInterval(t);
  }, [row, refresh]);

  const labels = useMemo(
    () => row && row !== "missing" ? parseLabels(row.speaker_labels) : {},
    [row],
  );
  const turns = useMemo(
    () => row && row !== "missing" && row.raw_json?.words?.length ? groupTurns(row.raw_json.words) : [],
    [row],
  );
  const terms = useMemo(() => matchTerms(highlight), [highlight]);

  if (row == null) return <div className="empty">Loading…</div>;
  if (row === "missing") return (
    <>
      <h1>404</h1>
      <p><Link to="/">back</Link></p>
    </>
  );

  const title = row.title?.trim() || row.filename;
  const hasCustomTitle = !!row.title && row.title.trim().length > 0 && row.title !== row.filename;
  const summary = (row.summary ?? "").trim();
  const pending = row.status === "queued" || row.status === "processing";

  const onSaveMeta = async (e: React.FormEvent) => {
    e.preventDefault();
    setSavingMeta(true);
    try {
      await updateMeta(row.id, { title: titleInput, summary: summaryInput });
      await refresh();
    } finally {
      setSavingMeta(false);
    }
  };

  const onDelete = async () => {
    setDeleting(true);
    try {
      await deleteTranscription(row.id);
      navigate("/");
    } catch {
      setActionError("Delete failed — the server may be unreachable.");
      setPendingDelete(false);
      setDeleting(false);
    }
  };

  const onCopyMarkdown = async () => {
    setCopyState("busy");
    try {
      await copyText(await fetchMarkdown(row.id));
      setCopyState("copied");
    } catch {
      setCopyState("failed");
    }
    setTimeout(() => setCopyState("idle"), 2500);
  };

  const onRetry = async () => {
    setRetrying(true);
    setActionError(null);
    try {
      await retryTranscription(row.id);
      await refresh();
    } catch (err) {
      setActionError((err as Error).message.includes("409")
        ? "The audio file is no longer on the server — upload it again to retry."
        : "Retry failed — the server may be unreachable.");
    } finally {
      setRetrying(false);
    }
  };

  // Unique speakers in order
  const speakers: string[] = [];
  const seen = new Set<string>();
  for (const t of turns) if (!seen.has(t.speakerId)) { seen.add(t.speakerId); speakers.push(t.speakerId); }

  return (
    <>
      <Link className="backlink bare" to="/">← All transcripts</Link>
      <header className="detail-hero">
        <h1>{title}</h1>
        <div className="detail-byline">
          <span><span className={`status ${row.status}`}>{row.status}</span></span>
          {hasCustomTitle && <span>{row.filename}</span>}
          <span>{row.mime_type}</span>
          <span>{fmtBytes(row.size_bytes)}</span>
          {row.word_count != null && row.word_count > 0 && <span>{fmtCount(row.word_count)} words</span>}
          {row.speaker_count != null && row.speaker_count > 0 && (
            <span>{row.speaker_count} speaker{row.speaker_count === 1 ? "" : "s"}</span>
          )}
          <span title={fmtAbsolute(row.created_at)}>{fmtRelative(row.created_at)}</span>
        </div>
        <div className="detail-actions">
          <button type="button" className="row-action danger" onClick={() => setPendingDelete(true)}>
            delete
          </button>
        </div>
        {actionError && <div className="error-banner" role="alert"><span>{actionError}</span></div>}
        <audio
          className="detail-audio"
          controls
          preload="none"
          src={`/api/transcriptions/${row.id}/audio`}
        />
        {summary && <div className="detail-summary"><Markdown source={summary} /></div>}
      </header>

      <div className={`meta-editor ${editorOpen ? "open" : ""}`}>
        <button type="button" className="meta-editor-summary" onClick={() => setEditorOpen((o) => !o)}>
          {editorOpen ? "− " : "+ "}Edit title &amp; summary
        </button>
        {editorOpen && (
          <form className="meta-form" onSubmit={onSaveMeta}>
            <div>
              <label htmlFor="meta-title">Title</label>
              <input
                id="meta-title"
                type="text"
                value={titleInput}
                onChange={(e) => setTitleInput(e.target.value)}
                placeholder={row.filename}
                autoComplete="off"
              />
            </div>
            <div>
              <label htmlFor="meta-summary">Summary</label>
              <textarea
                id="meta-summary"
                rows={4}
                value={summaryInput}
                onChange={(e) => setSummaryInput(e.target.value)}
                placeholder="A short description of this recording…"
              />
            </div>
            <div className="meta-actions">
              <button type="submit" className="btn btn-primary" disabled={savingMeta}>
                {savingMeta ? "Saving…" : "Save"}
              </button>
            </div>
          </form>
        )}
      </div>

      {row.status === "failed" && (
        <div className="detail-error">
          <strong>Transcription failed.</strong> {row.error ?? "unknown error"}
          <div className="detail-error-actions">
            <button type="button" className="btn" onClick={() => void onRetry()} disabled={retrying}>
              {retrying ? "Retrying…" : "Retry transcription"}
            </button>
          </div>
        </div>
      )}

      {row.status === "done" && row.raw_json && (
        <section className="manuscript">
          <div className="manuscript-head">
            <h2>Transcript</h2>
            <span className="lang">
              {row.raw_json.language_code} · {(row.raw_json.language_probability * 100).toFixed(0)}% · {fmtDuration(row.raw_json.audio_duration_secs)}
            </span>
            <span className="export-group">
              <button
                type="button"
                className={`btn${copyState === "copied" ? " ok" : ""}`}
                onClick={() => void onCopyMarkdown()}
                disabled={copyState === "busy"}
              >
                {copyState === "copied" ? "Copied ✓"
                  : copyState === "failed" ? "Copy failed"
                  : copyState === "busy" ? "Copying…"
                  : "Copy markdown"}
              </button>
              <a className="btn" href={`/api/transcriptions/${row.id}/markdown?download=1`}>
                Download .md
              </a>
              <a className="row-action" href={`/api/transcriptions/${row.id}/raw`} download>json</a>
            </span>
          </div>

          {speakers.length > 0 && (
            <div className="speakers">
              {speakers.map((sid) => (
                <SpeakerChip key={sid} rowId={row.id} sid={sid} initial={labels[sid] ?? ""} onSaved={refresh} />
              ))}
            </div>
          )}

          <div className="transcript">
            {turns.length > 0 ? (
              turns.map((turn, i) => (
                <div className="turn" key={i} style={{ ["--speaker-color" as never]: speakerColor(turn.speakerId) }}>
                  <div className="turn-gutter">
                    <span className="turn-speaker">{speakerDisplay(turn.speakerId, labels)}</span>
                    <span className="turn-time">{fmtTime(turn.start)}</span>
                  </div>
                  <div className="turn-body">
                    {turn.words.map((w, j) => {
                      if (w.type !== "word") return <span key={j}>{w.text}</span>;
                      const matched = isMatch(w.text, terms);
                      return (
                        <span
                          key={j}
                          className={`word${matched ? " match" : ""}`}
                          title={`${fmtTime(w.start)} – ${fmtTime(w.end)}`}
                        >
                          {w.text}
                        </span>
                      );
                    })}
                  </div>
                </div>
              ))
            ) : (
              <p>{row.text ?? ""}</p>
            )}
          </div>
        </section>
      )}

      {pending && (
        <section className="pending">
          <div className="pending-mark" aria-hidden="true"></div>
          <div>
            <strong>{row.status === "queued" ? "Queued for transcription" : "Transcribing"}</strong>
            <div className="pending-sub">This page refreshes itself every few seconds.</div>
          </div>
        </section>
      )}

      {pendingDelete && (
        <DeleteModal
          title={title}
          busy={deleting}
          onCancel={() => setPendingDelete(false)}
          onConfirm={onDelete}
        />
      )}
    </>
  );
}

function SpeakerChip({
  rowId, sid, initial, onSaved,
}: { rowId: string; sid: string; initial: string; onSaved: () => Promise<void> }) {
  const [value, setValue] = useState(initial);
  const [busy, setBusy] = useState(false);
  const color = speakerColor(sid);
  const placeholder = speakerDisplay(sid, {});

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      await setSpeakerLabel(rowId, sid, value);
      await onSaved();
    } finally {
      setBusy(false);
    }
  };

  return (
    <span className="speaker-chip" style={{ ["--speaker-color" as never]: color }}>
      <span className="glyph">{speakerGlyph(sid)}</span>
      <form onSubmit={onSubmit}>
        <input
          type="text"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder={placeholder}
          autoComplete="off"
        />
        <button type="submit" disabled={busy}>{busy ? "…" : "save"}</button>
      </form>
    </span>
  );
}
